import { hasDeliveryZones } from '@/lib/deliveryZones';
import { requestMenuGeocodeAddress } from '@/lib/checkout/deliveryGeocode';
import { requestMenuDeliveryQuote } from '@/lib/checkout/deliveryQuote';
import { resolveTipAmount, paymentMethodToPreviewLabel } from '@/lib/assistant/buildOrderPreview';

function deliveryWarning(delivery) {
  if (delivery.status === 'missing_comuna') return 'Falta comuna';
  if (delivery.status === 'out_of_zone') return 'Fuera de zona de delivery';
  if (delivery.status === 'needs_map') return 'Delivery por confirmar en mapa';
  if (delivery.status === 'missing_address') return 'Falta dirección de envío';
  if (delivery.status === 'loading') return null;
  return null;
}

function recomputePreviewTotals(preview, delivery, hints = {}) {
  const deliveryFee = delivery.status === 'ok' && typeof delivery.fee === 'number' ? delivery.fee : 0;
  const tip = resolveTipAmount(hints, preview.subtotal, deliveryFee);
  const total = preview.subtotal + deliveryFee + tip;
  const paymentMethod = hints.paymentMethod ? String(hints.paymentMethod) : preview.payment?.method;
  const cashAmount = hints.cashAmount ?? preview.payment?.cashAmount ?? null;
  const change =
    cashAmount != null && paymentMethod === 'efectivo' && cashAmount > total ? cashAmount - total : null;
  const warnings = [];
  const dw = deliveryWarning(delivery);
  if (dw) warnings.push(dw);
  return {
    ...preview,
    delivery,
    tip,
    total,
    payment: {
      method: paymentMethod,
      label: paymentMethodToPreviewLabel(paymentMethod),
      cashAmount,
      change,
    },
    warnings,
  };
}

/**
 * Geocodifica dirección y cotiza delivery para locales sin zonas por comuna.
 * @param {import('@/lib/assistant/buildOrderPreview').OrderPreview} preview
 * @param {string} businessId
 * @param {object|null|undefined} business
 * @param {object} [hints]
 * @returns {Promise<{ preview: import('@/lib/assistant/buildOrderPreview').OrderPreview, markerPosition: { lat: number, lng: number }|null, formattedAddress: string|null }>}
 */
export async function enrichOrderPreviewWithMapDelivery(preview, businessId, business, hints = {}) {
  if (!preview?.address || !businessId || hasDeliveryZones(business)) {
    return { preview, markerPosition: null, formattedAddress: null };
  }
  if (preview.delivery?.status !== 'needs_map' && preview.delivery?.status !== 'loading') {
    return { preview, markerPosition: null, formattedAddress: null };
  }

  try {
    const geo = await requestMenuGeocodeAddress(businessId, preview.address);
    const lat = Number(geo?.lat);
    const lng = Number(geo?.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      const delivery = {
        fee: null,
        status: 'out_of_zone',
        zoneName: null,
        hint: 'No ubicamos esa dirección. Revisá calle, número y comuna.',
      };
      return {
        preview: recomputePreviewTotals(preview, delivery, hints),
        markerPosition: null,
        formattedAddress: null,
      };
    }

    const quote = await requestMenuDeliveryQuote(businessId, lat, lng);
    if (quote?.within_radius === false) {
      const delivery = {
        fee: null,
        status: 'out_of_zone',
        zoneName: null,
        hint:
          typeof quote?.detail === 'string' && quote.detail.trim()
            ? quote.detail.trim()
            : 'Fuera del radio de cobertura',
      };
      return {
        preview: recomputePreviewTotals(preview, delivery, hints),
        markerPosition: { lat, lng },
        formattedAddress: String(geo?.formatted_address || '').trim() || null,
      };
    }

    const fee = typeof quote?.delivery_fee === 'number' ? quote.delivery_fee : 0;
    const delivery = {
      fee,
      status: 'ok',
      zoneName: null,
      hint: null,
      distanceKm: typeof quote?.distance_km === 'number' ? quote.distance_km : null,
    };
    const formattedAddress = String(geo?.formatted_address || '').trim() || preview.address;
    const nextPreview = recomputePreviewTotals(
      { ...preview, address: formattedAddress },
      delivery,
      hints
    );
    return {
      preview: nextPreview,
      markerPosition: { lat, lng },
      formattedAddress,
    };
  } catch {
    const delivery = {
      fee: null,
      status: 'needs_map',
      zoneName: null,
      hint: 'No pudimos calcular el delivery. Confirmalo en el mapa al cerrar el pedido.',
    };
    return {
      preview: recomputePreviewTotals(preview, delivery, hints),
      markerPosition: null,
      formattedAddress: null,
    };
  }
}

export function previewWithLoadingDelivery(preview) {
  if (!preview || preview.delivery?.status !== 'needs_map') return preview;
  return {
    ...preview,
    delivery: {
      ...preview.delivery,
      status: 'loading',
      hint: 'Calculando delivery…',
    },
    warnings: (preview.warnings || []).filter((w) => w !== 'Delivery por confirmar en mapa'),
  };
}
